import React from 'react';
import { Activity } from '../types';
import { SummaryCard } from '../components/SummaryCard';
import { StatusBadge } from '../components/StatusBadge';
import { PriorityBadge } from '../components/PriorityBadge';
import { ActivityIcon, CheckCircleIcon, AlertTriangleIcon, FlameIcon, EyeIcon } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
interface DashboardPageProps {
  activities: Activity[];
  onViewActivity: (id: string) => void;
}
export function DashboardPage({
  activities,
  onViewActivity
}: DashboardPageProps) {
  const activeStatuses = ['Baru', 'Berjalan', 'Menunggu Verifikasi'];
  const activeCount = activities.filter(a => activeStatuses.includes(a.status)).length;
  const completedCount = activities.filter(a => a.status === 'Selesai').length;
  const today = new Date().toISOString().split('T')[0];
  const overdueCount = activities.filter(a => a.dueDate < today && a.status !== 'Selesai' && a.status !== 'Dibatalkan').length;
  const highPriorityCount = activities.filter(a => a.priority === 'Tinggi' || a.priority === 'Sangat Tinggi').length;
  const statusCounts = activities.reduce((acc, curr) => {
    acc[curr.status] = (acc[curr.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const statusData = Object.keys(statusCounts).map(key => ({
    name: key,
    value: statusCounts[key]
  }));
  const COLORS = {
    Draft: '#64748B',
    Baru: '#00D4FF',
    Berjalan: '#0EA5E9',
    'Menunggu Verifikasi': '#FFB800',
    Tertunda: '#FF3B3B',
    Selesai: '#00FF88',
    Dibatalkan: '#475569'
  };
  const unitDataMap = activities.reduce((acc, curr) => {
    if (!acc[curr.unit]) {
      acc[curr.unit] = {
        total: 0,
        count: 0
      };
    }
    acc[curr.unit].total += curr.progressPercent;
    acc[curr.unit].count += 1;
    return acc;
  }, {} as Record<string, {
    total: number;
    count: number;
  }>);
  const unitData = Object.keys(unitDataMap).map(unit => ({
    name: unit.replace('Tim ', ''),
    progres: Math.round(unitDataMap[unit].total / unitDataMap[unit].count)
  }));
  const recentActivities = [...activities].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).slice(0, 5);
  const overdueActivities = activities.filter(a => a.dueDate < today && a.status !== 'Selesai' && a.status !== 'Dibatalkan').sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
  return <div className="space-y-6" data-id="element-499">
      {/* Date Header */}
      <div className="flex justify-between items-end" data-id="element-500">
        <div className="flex items-center space-x-3" data-id="element-501">
          <span className="flex items-center px-2 py-1 bg-[#00FF88]/10 border border-[#00FF88]/30 rounded text-xs font-bold text-[#00FF88] uppercase tracking-wider glow-green" data-id="element-502">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] mr-1.5 animate-pulse" data-id="element-503"></span>
            LIVE
          </span>
          <p className="text-[#94A3B8] text-sm" data-id="element-504">
            Data per{' '}
            {new Date().toLocaleDateString('id-ID', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-505">
        <SummaryCard title="Total Kegiatan Aktif" value={activeCount} icon={<ActivityIcon className="w-6 h-6" data-id="element-507" />} iconBgColor="bg-[#00D4FF]/10" iconColor="text-[#00D4FF]" borderColor="border-t-[#00D4FF]" data-id="element-506" />
        <SummaryCard title="Total Kegiatan Selesai" value={completedCount} icon={<CheckCircleIcon className="w-6 h-6" data-id="element-509" />} iconBgColor="bg-[#00FF88]/10" iconColor="text-[#00FF88]" borderColor="border-t-[#00FF88]" data-id="element-508" />
        <SummaryCard title="Kegiatan Overdue" value={overdueCount} icon={<AlertTriangleIcon className="w-6 h-6" data-id="element-511" />} iconBgColor="bg-[#FF3B3B]/10" iconColor="text-[#FF3B3B]" borderColor="border-t-[#FF3B3B]" data-id="element-510" />
        <SummaryCard title="Prioritas Tinggi" value={highPriorityCount} icon={<FlameIcon className="w-6 h-6" data-id="element-513" />} iconBgColor="bg-[#FFB800]/10" iconColor="text-[#FFB800]" borderColor="border-t-[#FFB800]" data-id="element-512" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-514">
        {/* Status Chart */}
        <div className="bg-[#111827] rounded shadow-lg border border-[#1E3A5F] p-5" data-id="element-515">
          <h3 className="text-sm font-semibold text-[#E2E8F0] mb-4 uppercase tracking-wide flex items-center" data-id="element-516">
            <span className="w-1 h-4 bg-[#00D4FF] mr-2" data-id="element-517"></span>
            SEBARAN STATUS KEGIATAN
          </h3>
          <div className="h-64" data-id="element-518">
            <ResponsiveContainer width="100%" height="100%" data-id="element-519">
              <PieChart data-id="element-520">
                <Pie data={statusData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" stroke="none" data-id="element-521">
                  {statusData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[entry.name as keyof typeof COLORS] || '#8884d8'} data-id="element-522" />)}
                </Pie>
                <Tooltip contentStyle={{
                backgroundColor: '#1A2332',
                border: '1px solid #1E3A5F',
                borderRadius: '4px',
                color: '#E2E8F0'
              }} itemStyle={{
                color: '#E2E8F0'
              }} formatter={value => [`${value} Kegiatan`, 'Jumlah']} data-id="element-523" />
                <Legend verticalAlign="bottom" height={36} wrapperStyle={{
                color: '#E2E8F0',
                fontSize: '12px'
              }} data-id="element-524" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Progress Chart */}
        <div className="bg-[#111827] rounded shadow-lg border border-[#1E3A5F] p-5" data-id="element-525">
          <h3 className="text-sm font-semibold text-[#E2E8F0] mb-4 uppercase tracking-wide flex items-center" data-id="element-526">
            <span className="w-1 h-4 bg-[#00D4FF] mr-2" data-id="element-527"></span>
            RATA-RATA PROGRES PER UNIT (%)
          </h3>
          <div className="h-64" data-id="element-528">
            <ResponsiveContainer width="100%" height="100%" data-id="element-529">
              <BarChart data={unitData} margin={{
              top: 10,
              right: 10,
              left: -20,
              bottom: 0
            }} data-id="element-530">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1E3A5F" data-id="element-531" />
                <XAxis dataKey="name" tick={{
                fontSize: 12,
                fill: '#94A3B8'
              }} axisLine={{
                stroke: '#1E3A5F'
              }} tickLine={false} data-id="element-532" />
                <YAxis tick={{
                fontSize: 12,
                fill: '#94A3B8'
              }} axisLine={{
                stroke: '#1E3A5F'
              }} tickLine={false} domain={[0, 100]} data-id="element-533" />
                <Tooltip cursor={{
                fill: '#1A2332'
              }} contentStyle={{
                backgroundColor: '#1A2332',
                border: '1px solid #1E3A5F',
                borderRadius: '4px',
                color: '#E2E8F0'
              }} formatter={value => [`${value}%`, 'Progres']} data-id="element-534" />
                <Bar dataKey="progres" fill="#00D4FF" radius={[2, 2, 0, 0]} barSize={30} data-id="element-535" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Tables Area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-536">
        {/* Recent Activities */}
        <div className="bg-[#111827] rounded shadow-lg border border-[#1E3A5F] overflow-hidden flex flex-col" data-id="element-537">
          <div className="p-4 border-b border-[#1E3A5F] bg-[#0F1629]" data-id="element-538">
            <h3 className="text-sm font-semibold text-[#E2E8F0] uppercase tracking-wide flex items-center" data-id="element-539">
              <span className="w-1 h-4 bg-[#00D4FF] mr-2" data-id="element-540"></span>
              KEGIATAN TERBARU
            </h3>
          </div>
          <div className="overflow-x-auto flex-1" data-id="element-541">
            <table className="w-full text-sm text-left" data-id="element-542">
              <thead className="text-xs text-[#94A3B8] uppercase bg-[#0B0F1A] border-b border-[#1E3A5F]" data-id="element-543">
                <tr data-id="element-544">
                  <th className="px-4 py-3 font-semibold" data-id="element-545">KODE & JUDUL</th>
                  <th className="px-4 py-3 font-semibold" data-id="element-546">STATUS</th>
                  <th className="px-4 py-3 font-semibold text-center" data-id="element-547">AKSI</th>
                </tr>
              </thead>
              <tbody data-id="element-548">
                {recentActivities.map(act => <tr key={act.id} className="border-b border-[#1E3A5F]/50 hover:bg-[#1A2332] transition-colors" data-id="element-549">
                    <td className="px-4 py-3" data-id="element-550">
                      <div className="font-mono text-[#00D4FF] text-xs mb-1" data-id="element-551">
                        {act.code}
                      </div>
                      <div className="text-sm text-[#E2E8F0] truncate max-w-[200px]" data-id="element-552">
                        {act.title}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-553">
                      <StatusBadge status={act.status} data-id="element-554" />
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-555">
                      <button onClick={() => onViewActivity(act.id)} className="text-[#00D4FF]/60 hover:text-[#00D4FF] p-1.5 rounded hover:bg-[#00D4FF]/10 transition-colors" title="Lihat Detail" data-id="element-556">
                        <EyeIcon className="w-4 h-4" data-id="element-557" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        {/* Overdue Activities */}
        <div className="bg-[#111827] rounded shadow-lg border border-[#1E3A5F] overflow-hidden flex flex-col" data-id="element-558">
          <div className="p-4 border-b border-[#1E3A5F] bg-[#0F1629] flex justify-between items-center" data-id="element-559">
            <h3 className="text-sm font-semibold text-[#FF3B3B] uppercase tracking-wide flex items-center" data-id="element-560">
              <AlertTriangleIcon className="w-4 h-4 mr-2" data-id="element-561" />
              KEGIATAN OVERDUE
            </h3>
            <span className="bg-[#FF3B3B]/20 text-[#FF3B3B] border border-[#FF3B3B]/30 text-xs font-bold px-2 py-0.5 rounded glow-red" data-id="element-562">
              {overdueActivities.length}
            </span>
          </div>
          <div className="overflow-x-auto flex-1" data-id="element-563">
            {overdueActivities.length > 0 ? <table className="w-full text-sm text-left" data-id="element-564">
                <thead className="text-xs text-[#94A3B8] uppercase bg-[#0B0F1A] border-b border-[#1E3A5F]" data-id="element-565">
                  <tr data-id="element-566">
                    <th className="px-4 py-3 font-semibold" data-id="element-567">KEGIATAN</th>
                    <th className="px-4 py-3 font-semibold" data-id="element-568">DEADLINE</th>
                    <th className="px-4 py-3 font-semibold text-center" data-id="element-569">
                      AKSI
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-570">
                  {overdueActivities.map(act => <tr key={act.id} className="border-b border-[#1E3A5F]/50 hover:bg-[#1A2332] transition-colors border-l-2 border-l-[#FF3B3B] bg-[#FF3B3B]/5" data-id="element-571">
                      <td className="px-4 py-3" data-id="element-572">
                        <div className="font-medium text-[#E2E8F0] text-sm truncate max-w-[200px] mb-1" data-id="element-573">
                          {act.title}
                        </div>
                        <div className="text-xs text-[#94A3B8]" data-id="element-574">{act.unit}</div>
                      </td>
                      <td className="px-4 py-3" data-id="element-575">
                        <span className="text-[#FF3B3B] font-mono text-xs font-bold glow-red" data-id="element-576">
                          {act.dueDate}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center" data-id="element-577">
                        <button onClick={() => onViewActivity(act.id)} className="text-[#00D4FF]/60 hover:text-[#00D4FF] p-1.5 rounded hover:bg-[#00D4FF]/10 transition-colors" data-id="element-578">
                          <EyeIcon className="w-4 h-4" data-id="element-579" />
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table> : <div className="p-8 text-center text-[#94A3B8]" data-id="element-580">
                <CheckCircleIcon className="w-12 h-12 text-[#00FF88]/50 mx-auto mb-3" data-id="element-581" />
                <p className="text-sm" data-id="element-582">
                  Tidak ada kegiatan yang melewati batas waktu.
                </p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}